import { ModalProps } from "@material-ui/core";
import React, { useEffect } from "react";
import { getProductDetailsPath } from "../../../helpers/url";
import ProductDetails from "../ProductDetails";
import {
  CustomModalTransition,
  ModalContent,
  CustomBackdrop,
  CustomModal,
  CustomBackdropOverlapsHeader
} from "./styles";
import { replaceHistoryPath } from "./utils";
import { useLocation } from "@reach/router";
import useLocalizedLocationPath from "../../../hooks/use-localized-location-path";
import ModalCloseButton from "../../../components/basic-modal/ModalCloseButton";
import useProductQuery, {
  ProductQuerySelectorType
} from "../../../hooks/use-product-query";
import { usePrevious } from "react-use";

type Props = Omit<ModalProps, "children"> & {
  productSelector: ProductQuerySelectorType;
  zIndex?: number;
  headervariant?: string;
};

export default function ProductDetailsModal({
  open,
  productSelector,
  zIndex,
  headervariant,
  ...modalProps
}: Props) {
  const location = useLocation();
  const getLocalizedPath = useLocalizedLocationPath();
  const previousOpen = usePrevious(open);

  // Fetch product data when modal is open
  const { data: product } = useProductQuery(productSelector, { enabled: open });

  /**
   * Update client-side history url when modal is open.
   * If user refresh or share the page then it will load the standalone product details page.
   */
  useEffect(() => {
    if (!product) return;
    if (open) {
      // Replace path with pdp pathname
      replaceHistoryPath(
        getLocalizedPath(
          `${getProductDetailsPath(product.productType, product.slug)}/`
        )
      );

      // Listen to next history back
      window.addEventListener(
        "popstate",
        () => modalProps?.onClose({}, "backdropClick"),
        { once: true } // Remove after user goes back once
      );
    } else if (!open && previousOpen) {
      // Revert to original path where modal was opened
      replaceHistoryPath(`${location.pathname}${location.search}`);
    }
  }, [open, previousOpen, product]);

  return (
    <CustomModal
      open={open}
      {...modalProps}
      closeAfterTransition
      disableAutoFocus
      BackdropComponent={
        headervariant === "overlaps"
          ? CustomBackdropOverlapsHeader
          : CustomBackdrop
      }
      BackdropProps={{
        timeout: {
          enter: 250,
          exit: 250
        }
      }}
      style={zIndex ? { zIndex } : null}
      overlapsHeader={headervariant === "overlaps"}
    >
      {/* Animated modal */}
      <CustomModalTransition in={open}>
        <ModalContent>
          {product && <ProductDetails product={product} isModal />}
        </ModalContent>

        {/* Close button */}
        <ModalCloseButton
          variant="dark"
          onClick={e => modalProps.onClose(e, "backdropClick")}
          style={{ zIndex: 200 }}
        />
      </CustomModalTransition>
    </CustomModal>
  );
}
